import { MapPin, DollarSign, Building2, TrendingUp, Home, Layers, Landmark, Scale, Calendar, Users } from 'lucide-react';

const img = (id) => `https://images.unsplash.com/photo-${id}?w=800&q=85`;

const createSuburbArticles = (suburb, suburbSlug, config) => {
  const { distance, keyInfra, zones, mainStreets, priceRange, keyDriver, uniqueFactor, comparison, nearbyAmenity } = config;
  return [
    {
      id: `${suburbSlug}-property-development-site-guide`,
      category: 'Local Guide',
      title: `Is Your ${suburb} Property a Development Site? Signs to Look For`,
      excerpt: `${suburb} is attracting growing developer interest. Here's how to tell if your property has development potential.`,
      date: '2026-04-06',
      readTime: '7 min read',
      image: img('1697767217116-465fa27e4521'),
      icon: MapPin,
      metaDescription: `Is your ${suburb} property a development site? Learn the signs that your land has development potential in this Brisbane suburb ${distance} from the CBD.`,
      content: `
## ${suburb}: Development Potential

${suburb} sits ${distance} from the Brisbane CBD. ${uniqueFactor}

If you own property in ${suburb}, there's a good chance it could be worth significantly more as a development site than as a residential home.

## Key Signs Your ${suburb} Property Has Development Value

### 1. Your Zoning Allows Higher Density

Under the Brisbane City Plan 2014, ${suburb} contains several zones:

| Zone | Typical Height | What Can Be Built |
|---|---|---|
${zones.map(z => `| ${z.name} | ${z.height} | ${z.product} |`).join('\n')}

Check your specific zoning at [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/).

### 2. Your Block is Suitable for Development

Developers in ${suburb} look for:
- **Blocks of 400m²+** in medium-to-high density zones
- **Wide frontage** (15m+ preferred) for efficient building design
- **Corner blocks** with dual street frontage — these command a 20-35% premium
- **Flat or gently sloping** topography for straightforward construction

### 3. You're Near Key Infrastructure

${keyInfra}

### 4. Developers Have Already Approached You

If you've received unsolicited letters or approaches from developers, it confirms market interest — but never accept without independent advice. A specialist agent creates competition that drives 15-30% higher prices.

> **Pro tip**: Properties in ${suburb} that would sell for a standard residential price regularly achieve 40-100% more when marketed as development sites to qualified developer buyers.

## What's Driving Developer Demand in ${suburb}

- ${keyDriver}
- Strong end-buyer demand for inner-city apartment and townhouse living
- Limited remaining development sites creating scarcity premium
- ${nearbyAmenity}
- South East Queensland's sustained population growth and housing undersupply

## How to Find Out Your Property's Potential

ACRES provides complimentary development site assessments for ${suburb} property owners covering zoning, constraints, estimated yield, and realistic price expectations.

## Frequently Asked Questions

**Q: How much more could my ${suburb} property be worth as a development site?**
Properties in medium-to-high density zones typically sell for 40-100% above standard residential value. The premium depends on zoning, block size, and site characteristics.

**Q: Do I need to get development approval before selling?**
No. Most developers prefer to design their own project. A site with an existing DA can attract a 10-20% premium but isn't required.

**Q: How long will it take to sell my ${suburb} property as a development site?**
Development site sales typically take 2-4 months to secure a contract, with settlement periods of 6-18 months depending on conditions.

---

*ACRES provides complimentary development site assessments in ${suburb}. Call 07 3096 0542 or email daniel@acres.au.*
      `,
      faq: [
        { q: `Is my ${suburb} property a development site?`, a: `If your property is zoned for medium or high density in ${suburb}, it likely has significant development value. Corner blocks and lots over 500m² are particularly attractive to developers.` },
        { q: `How much more is my ${suburb} land worth to a developer?`, a: `Properties in medium-to-high density zones can be worth 40-100% more as development sites depending on zoning, block size, and site characteristics.` },
        { q: `Are developers actively buying in ${suburb}?`, a: `Yes. ${suburb} is an active development site market driven by ${keyDriver.toLowerCase()} and sustained end-buyer demand.` },
      ],
    },
    {
      id: `${suburbSlug}-land-prices-what-developers-pay`,
      category: 'Local Guide',
      title: `What Developers Are Paying for Land in ${suburb}`,
      excerpt: `Current pricing benchmarks for development sites in ${suburb} and the factors driving land values.`,
      date: '2026-04-04',
      readTime: '6 min read',
      image: img('1672643405411-10cdbebfc3df'),
      icon: DollarSign,
      metaDescription: `What are developers paying for land in ${suburb} Brisbane? Current development site pricing, benchmarks, and what drives land values.`,
      content: `
## ${suburb} Development Site Pricing

${suburb} development sites attract growing demand from developers targeting Brisbane's middle-ring growth corridor. ${uniqueFactor}

## Current Price Benchmarks

| Site Type | Typical Size | Price Range | Price per m² |
|---|---|---|---|
${priceRange.map(p => `| ${p.type} | ${p.size} | ${p.range} | ${p.perm2} |`).join('\n')}

These figures represent what developers will pay — often significantly above standard residential market value.

## What Drives Price Variation in ${suburb}

### Zoning and Height
The single biggest price driver. Higher density zoning allows more dwellings, generating more revenue, which means developers can pay more for the land.

### Location Within the Suburb
Properties on or near ${mainStreets} command premiums due to higher density zoning, better transport access, and stronger end-buyer appeal.

### Site Configuration
Wide frontage (15m+), flat topography, and corner positions increase value. Narrow, deep blocks or steeply sloping sites attract fewer buyers.

### Existing Approvals
A site with a current DA typically sells for 10-20% more than an unapproved site, as the buyer avoids months of approval risk and holding costs.

## How ${suburb} Compares

${comparison}

> **Key insight**: Properties that would sell for a standard residential price regularly achieve significant premiums when marketed correctly as development sites.

## Frequently Asked Questions

**Q: Are ${suburb} development site prices still rising?**
Prices have shown consistent growth driven by limited supply and strong demand. Structural factors support continued pricing strength.

**Q: How do I know if a developer's offer is fair?**
Never accept an unsolicited offer without independent advice. A specialist agent tests the broader market and creates competitive tension.

**Q: How do I get an accurate price for my ${suburb} site?**
Contact ACRES for a complimentary development site appraisal considering zoning, yield, feasibility, and current demand.

---

*For a confidential ${suburb} development site appraisal, contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `What are developers paying for land in ${suburb}?`, a: `Pricing varies by zone. ${priceRange[1] ? `${priceRange[1].type} sites typically sell for ${priceRange[1].range}.` : 'Contact ACRES for current benchmarks.'}` },
        { q: `Are ${suburb} development site prices still rising?`, a: `Yes. Limited supply and strong demand continue to support price growth in ${suburb}.` },
        { q: `How much more will a developer pay vs a home buyer in ${suburb}?`, a: `Developers typically pay 40-100% above residential value depending on zoning. A specialist agent can provide an accurate assessment.` },
      ],
    },
    {
      id: `${suburbSlug}-zoning-guide-what-you-can-build`,
      category: 'Local Guide',
      title: `${suburb} Zoning Guide: What You Can Build on Your Block`,
      excerpt: `A plain-English breakdown of ${suburb}'s zoning and what it means for your property's development value.`,
      date: '2026-04-02',
      readTime: '8 min read',
      image: img('1606509463400-5ce6440c53e9'),
      icon: Scale,
      metaDescription: `${suburb} Brisbane zoning guide for property owners. Understanding your zoning, building heights, and development potential under Brisbane City Plan 2014.`,
      content: `
## Understanding ${suburb}'s Zoning

${suburb}'s zoning is governed by the **Brisbane City Plan 2014** and any applicable neighbourhood plan provisions. Your zoning determines what can be built on your property — and how much a developer will pay for it.

## Zones in ${suburb}

${zones.map(z => `### ${z.name}
- **Height**: ${z.height}
- **What you can build**: ${z.product}
- **Development premium**: ${z.premium || 'Varies by site'}
`).join('\n')}

## How Zoning Affects Your Property Value

| Scenario | Zoning | Potential Yield | Value Impact |
|---|---|---|---|
| Standard home | Low Density | 1 dwelling | Residential baseline |
| Townhouse site | Low-Medium Density | 3-5 townhouses | 30-50% premium |
| Apartment site | Medium Density | 8-15 apartments | 50-80% premium |
| Larger apartment | High Density | 20-40+ apartments | 80-150%+ premium |

Same block size — dramatically different values based on zoning alone.

## Key Overlays in ${suburb}

- **Flood overlay**: Check for flood mapping near waterways — affects ground-floor design and may add costs
- **Heritage/Character overlay**: May restrict demolition of pre-1947 houses and limit height
- **Infrastructure easements**: Sewer, stormwater, or power may reduce developable area

## How to Check Your ${suburb} Zoning

1. Visit [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/)
2. Search your ${suburb} address
3. Check **Zoning**, **Overlays**, and **Neighbourhood Plans** layers

> **Pro tip**: A professional assessment gives you the complete picture of what your zoning means in practice — including any overlays or provisions that modify the base zone.

## Frequently Asked Questions

**Q: How do I find my exact zoning in ${suburb}?**
Use Brisbane City Plan Interactive Mapping. Search your address and check the Zoning layer. ACRES provides free professional interpretations.

**Q: Can my ${suburb} zoning change?**
Yes. Council reviews the City Plan periodically. Properties near infrastructure are candidates for future upzoning.

**Q: Does higher zoning guarantee maximum height?**
Not always. Overlays and neighbourhood plan provisions may limit achievable height.

---

*Not sure what your ${suburb} zoning means? ACRES provides free assessments. Call 07 3096 0542.*
      `,
      faq: [
        { q: `What zoning does my ${suburb} property have?`, a: `${suburb} has a mix of residential zones. Check cityplan.brisbane.qld.gov.au for your specific property. ACRES provides free zoning assessments.` },
        { q: `What can I build on my ${suburb} block?`, a: `It depends on your zone. ${zones[0] ? `${zones[0].name} allows ${zones[0].product.toLowerCase()}.` : ''} Higher zones allow apartments and mixed-use developments.` },
        { q: `Could my ${suburb} property be upzoned?`, a: `Properties near infrastructure and transport are candidates for upzoning in future City Plan reviews.` },
      ],
    },
    {
      id: `${suburbSlug}-infrastructure-driving-property-values`,
      category: 'Local Guide',
      title: `How Infrastructure Is Driving ${suburb} Property Values`,
      excerpt: `Key infrastructure and amenity driving development demand and property values in ${suburb}.`,
      date: '2026-04-28',
      readTime: '6 min read',
      image: img('1656501375517-67129e3eed14'),
      icon: TrendingUp,
      metaDescription: `How infrastructure and amenity are driving ${suburb} Brisbane property values. Impact on land prices, development demand, and what it means for owners.`,
      content: `
## Infrastructure Driving ${suburb}'s Growth

${keyInfra}

For property owners in ${suburb}, infrastructure investment is the most reliable indicator of future value growth.

## How Infrastructure Affects Development Site Values

| Infrastructure Type | Typical Value Uplift | Mechanism |
|---|---|---|
| Rail station (within 800m) | 10-25% | Higher density + end-buyer premium |
| Bus rapid transit | 5-15% | Improved connectivity |
| Major retail / commercial | 5-10% | Lifestyle amenity |
| Hospital / University | 8-15% | Employment + housing demand |
| Road upgrades | 3-8% | Improved access |

For development sites, the uplift is amplified because infrastructure proximity triggers higher density allowances and stronger end-product pricing.

## What This Means for ${suburb} Property Owners

- Your land is likely worth more now than 3 years ago
- Development site premiums above residential value are increasing
- Multiple buyer types are competing for available sites
- Current timing is favourable for sellers

> **Important**: Infrastructure-driven value growth typically continues for 5-10 years after project completion as the surrounding precinct matures.

## Frequently Asked Questions

**Q: Has infrastructure already increased ${suburb} property values?**
Yes. Values have risen as infrastructure projects progress and connectivity improves.

**Q: Will values keep rising?**
Historical evidence suggests values continue rising 5-10 years post-completion as precincts mature.

**Q: Should I sell now or wait?**
Current conditions are favourable. ACRES can advise on optimal timing for your circumstances.

---

*Understand how infrastructure affects your ${suburb} property. Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How does infrastructure affect ${suburb} property values?`, a: `Major infrastructure increases property values by 5-25%. Development sites near transport and amenity see the greatest uplift.` },
        { q: `Will ${suburb} property values keep rising?`, a: `Infrastructure-driven growth typically continues 5-10 years after completion. Fundamentals remain strong for ${suburb}.` },
        { q: `Should I sell my ${suburb} property now or wait?`, a: `Current conditions are favourable with strong demand. ACRES can advise on timing.` },
      ],
    },
    {
      id: `selling-${suburbSlug}-home-to-developer-guide`,
      category: 'Local Guide',
      title: `Selling Your ${suburb} Home to a Developer: A Complete Guide`,
      excerpt: `Step-by-step process for selling your ${suburb} property as a development site for maximum value.`,
      date: '2026-04-28',
      readTime: '7 min read',
      image: img('1513880989635-6eb491ce7f5b'),
      icon: Home,
      metaDescription: `How to sell your ${suburb} home to a developer. Complete guide covering preparation, pricing, agent selection, and achieving the best price.`,
      content: `
## Why ${suburb} Sellers Should Consider a Development Sale

If your ${suburb} property sits on land zoned for medium or high density, selling to a developer could unlock 40-100% more value than a standard residential sale.

## Step 1: Understand Your Development Potential

- **Check your zoning** at [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/)
- **Measure your block** — lot size, frontage width, and shape affect yield
- **Note key features** — corner position, transport proximity, main road frontage
- **Identify constraints** — overlays, easements, heritage or character listings

## Step 2: Choose a Specialist Agent

Look for:
- Development site experience in ${suburb} and Brisbane's inner ring
- An active database of developer buyers
- Knowledge of recent comparable sales
- A clear marketing strategy targeting developer buyers

> **Warning**: Unsolicited developer offers are almost always below market value. A specialist agent achieves 15-30% more through competitive tension.

## Step 3: Prepare Your Property

### Essential:
- **Boundary survey** ($2,000-$4,000)
- **Title search** and encumbrance review
- **Planning summary** — zoning, height limits, estimated yield

### Valuable additions:
- **Town planning assessment** ($3,000-$5,000)
- **Concept design** ($5,000-$10,000)

Don't invest in house renovations — developers will demolish regardless.

## Step 4: Market Through an EOI Campaign

1. Agent contacts developer database directly
2. Information memorandum prepared with site details
3. Site inspections with qualified buyers
4. Offer deadline creates competitive tension
5. Negotiate with strongest offers

## Step 5: Negotiate Terms

- **Deposit**: 5-10% (push for 10%)
- **Conditions**: DA, finance, due diligence with clear timeframes
- **Settlement**: 6-18 months typical
- **Sunset clause**: Deadline for condition satisfaction

## Frequently Asked Questions

**Q: Can I stay in my ${suburb} home during the sale process?**
Yes. You remain in the property throughout. Settlement is typically 6-18 months after contract.

**Q: How long does the process take?**
2-4 months to contract, then 6-18 months conditional, then 4-8 weeks to settlement.

**Q: What commission does a development site agent charge?**
Typically 1.5-2.5%. Given the higher prices achieved, the net outcome is much better.

---

*Ready to explore your ${suburb} property's potential? Call ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How do I sell my ${suburb} home to a developer?`, a: `Engage a specialist agent, prepare with survey and planning assessment, market through an EOI, and negotiate terms as well as price.` },
        { q: `Can I live in my ${suburb} home while selling to a developer?`, a: `Yes. You remain throughout. Settlement is typically 6-18 months after contract.` },
        { q: `What commission does a development site agent charge in ${suburb}?`, a: `Typically 1.5-2.5%. Given premiums of 40-100% above residential value, the net outcome is significantly better.` },
      ],
    },
    {
      id: `${suburbSlug}-development-hotspots`,
      category: 'Local Guide',
      title: `${suburb} Development Hotspots: Where Developers Want to Buy`,
      excerpt: `The specific streets and pockets within ${suburb} attracting the strongest developer demand.`,
      date: '2026-04-26',
      readTime: '5 min read',
      image: img('1730329944265-ab697e1a631c'),
      icon: Layers,
      metaDescription: `${suburb} development hotspots. Which streets and areas are attracting the most developer interest in ${suburb} Brisbane?`,
      content: `
## Where Developers Are Buying in ${suburb}

Not all streets in ${suburb} are equal for development demand. Developers target specific pockets based on zoning, infrastructure, and end-buyer appeal.

## Primary Hotspots

Properties along and near **${mainStreets}** are the most sought-after, offering:
- Higher density zoning and height allowances
- Transport connectivity and amenity access
- Strong end-buyer demand for completed apartments and townhouses

## Secondary Hotspots

Quieter residential streets in MDR zones offer:
- More affordable entry points for boutique developers
- Appeal to owner-occupier end-buyers seeking quieter locations
- Potential for medium-density townhouse and apartment projects

## What Makes a Street a Hotspot

| Factor | Impact on Developer Demand |
|---|---|
| HDR or Centre zoning | Very High — maximum yield |
| MDR zoning | High — sweet spot for many projects |
| Corner position | High — design and access advantages |
| Transport proximity | High — end-buyer premium |
| LMR with large lots | Moderate — townhouse potential |
| Character overlay | Lower — restrictions limit options |

## Emerging Opportunities

Watch for streets where council has increased density, adjacent streets have seen successful developments, or infrastructure projects are nearing completion. Early sellers in emerging hotspots often achieve the best outcomes.

## Frequently Asked Questions

**Q: My ${suburb} property isn't on a main road — do developers want it?**
Yes. Many developers prefer quieter streets for residential projects targeting owner-occupiers.

**Q: Are developers only buying large sites?**
No. Standard 400-600m² blocks in suitable zones attract strong interest. Site consolidation is also common.

**Q: How do I know if my street has developer demand?**
Contact ACRES for a free assessment. We monitor activity across every ${suburb} street.

---

*Is your ${suburb} street a development hotspot? Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Which ${suburb} streets are developers buying on?`, a: `Properties along ${mainStreets} see the strongest demand. Quieter MDR-zoned streets also attract boutique developers.` },
        { q: `Do developers buy on quiet streets in ${suburb}?`, a: `Yes. Many developers prefer quieter streets for projects targeting owner-occupiers.` },
        { q: `Are developers only buying large blocks in ${suburb}?`, a: `No. Standard 400-600m² blocks in MDR zones are actively sought. Consolidation is also common.` },
      ],
    },
    {
      id: `future-of-${suburbSlug}-council-plans`,
      category: 'Local Guide',
      title: `The Future of ${suburb}: Council Plans & What They Mean for Landowners`,
      excerpt: `How evolving council policy and infrastructure investment are shaping ${suburb}'s development future.`,
      date: '2026-04-24',
      readTime: '6 min read',
      image: img('1672626881472-b96d3eb5445e'),
      icon: Landmark,
      metaDescription: `The future of ${suburb} Brisbane. How council planning changes and infrastructure are shaping the suburb for property owners.`,
      content: `
## ${suburb}'s Evolving Landscape

${suburb} continues to evolve as Brisbane grows and demand for well-located housing intensifies. Council planning, infrastructure, and demographic shifts are shaping the suburb's future.

## What's Driving Change

### Population Growth
South East Queensland adds ~100,000 people per year. Suburbs like ${suburb} absorb significant growth through medium and high-density development.

### Infrastructure Investment
${keyInfra}

### Planning Evolution
Council reviews the City Plan periodically. ${suburb} may see continued refinement of density provisions along transport corridors and near activity centres.

## What This Means for Property Owners

| Property Location | Likely Trajectory | Action |
|---|---|---|
| Near transport corridor | Density increases likely | Strong position |
| MDR zone, no constraints | Stable to increasing | Market favourable |
| Character/heritage area | Likely restricted | Assess best approach |
| Near emerging infrastructure | Potential upzoning | Consider timing |

## Looking Ahead

| Timeframe | Expected Impact |
|---|---|
| 2025-2027 | Infrastructure benefits materialise |
| 2027-2030 | Precinct maturation, continued demand |
| 2030-2035 | Established suburban premium |

## Frequently Asked Questions

**Q: Will ${suburb} be further upzoned?**
Possible near transport and growth corridors. Council reviews the City Plan periodically.

**Q: Should I wait for planning changes?**
Current zoning supports strong values. Waiting carries uncertainty. ACRES can advise.

**Q: How do planning changes affect value?**
Density increases raise development site values. Character restrictions maintain current levels.

---

*Understand ${suburb}'s future. Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Will ${suburb} be further upzoned?`, a: `Possible near transport corridors. Council reviews the City Plan periodically.` },
        { q: `What does the future hold for ${suburb} property values?`, a: `Strong fundamentals including population growth, infrastructure, and limited supply support continued appreciation.` },
        { q: `Should I sell my ${suburb} property now or wait?`, a: `Current conditions are favourable. ACRES can advise on timing.` },
      ],
    },
    {
      id: `${suburbSlug}-corner-blocks-large-lots`,
      category: 'Local Guide',
      title: `${suburb} Corner Blocks & Large Lots: Why Developers Pay More`,
      excerpt: `Corner blocks and larger lots in ${suburb} command significant premiums from developer buyers.`,
      date: '2026-04-22',
      readTime: '5 min read',
      image: img('1557518016-95747c0eef63'),
      icon: Building2,
      metaDescription: `Why developers pay more for corner blocks and large lots in ${suburb} Brisbane. Premium pricing and what makes your block attractive.`,
      content: `
## The Premium for Corners and Large Lots in ${suburb}

Corner blocks and large lots command significant premiums because they offer developers design, yield, and market advantages.

## Why Corner Blocks Are Worth More

- **Dual street frontage** — separate pedestrian and vehicle access
- **Better apartment layouts** — more natural light and cross-ventilation
- **More attractive building forms** — engaging with both streetscapes
- **Higher density allowances** in some planning provisions

Corner blocks in ${suburb} typically command a **20-35% premium** over comparable mid-block sites.

## Why Large Lots Are Valuable

Large lots (700m²+) offer:
- Greater yield on a single title
- No amalgamation risk or neighbour negotiations
- More efficient building design
- Faster project commencement

## Site Consolidation

If your property adjoins another potential development site, a coordinated sale can increase total value by 10-20%. ACRES facilitates these discussions confidentially.

## Frequently Asked Questions

**Q: How much more is a corner block worth in ${suburb}?**
Typically 20-35% more than a comparable mid-block site in the same zone.

**Q: Is my standard block still a development site?**
Yes, if zoned for medium or high density.

**Q: Can I coordinate a sale with my neighbour?**
Yes. ACRES facilitates confidential multi-vendor sales.

---

*Own a corner block or large lot in ${suburb}? Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How much more is a corner block worth in ${suburb}?`, a: `Typically 20-35% more than mid-block sites in the same zone.` },
        { q: `Are large lots particularly valuable in ${suburb}?`, a: `Yes. Large lots (700m²+) are rare and command significant premiums.` },
        { q: `Can I sell my ${suburb} property with my neighbour's?`, a: `Yes. ACRES facilitates confidential multi-vendor sales. Combined value is typically 10-20% higher.` },
      ],
    },
    {
      id: `${suburbSlug}-property-market-2025`,
      category: 'Local Guide',
      title: `${suburb} Property Market 2026: Development Site Demand & Pricing`,
      excerpt: `Current market conditions for ${suburb} development sites and where the market is heading.`,
      date: '2026-04-20',
      readTime: '6 min read',
      image: img('1659563159297-6f724e125026'),
      icon: Calendar,
      metaDescription: `${suburb} property market 2025. Development site demand, pricing trends, and market outlook for sellers.`,
      content: `
## Market Overview: ${suburb} 2025

${suburb}'s development site market in 2026 features growing demand and tightening supply. The suburb's positioning and amenity continue to attract developers.

## Who's Buying
- Boutique developers building 10-30 apartment or townhouse projects
- National developers seeking medium-scale sites
- Townhouse specialists targeting the inner-ring terrace home market
- Build-to-rent operators seeking well-located rental sites

## Market Conditions

| Indicator | Status | Implication |
|---|---|---|
| Developer demand | Strong | Multiple buyers for quality sites |
| Site supply | Tightening | Scarcity supporting pricing |
| End-buyer demand | Resilient | Developer confidence in sales |
| Construction costs | Elevated | Moderating margins but not deterring acquisitions |
| Off-market activity | Increasing | Many transactions before public marketing |

## Outlook

Current conditions are favourable:
- Strong demand fundamentals
- Limited remaining supply
- Infrastructure investment continuing
- End-buyer market resilient

> **Bottom line**: ${suburb} is a seller's market for development sites. Well-prepared properties achieve premium outcomes.

## Frequently Asked Questions

**Q: Is 2025 a good time to sell in ${suburb}?**
Yes. Demand is strong, supply is limited, and fundamentals continue to improve.

**Q: Will prices keep rising?**
Structural factors support continued pricing strength.

**Q: How many developers are active in ${suburb}?**
Multiple developers are actively seeking sites. Competition is strong.

---

*For a current ${suburb} market assessment, contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Is 2025 a good time to sell in ${suburb}?`, a: `Yes. Strong demand, limited supply, and improving fundamentals make conditions favourable.` },
        { q: `Are ${suburb} development site prices still rising?`, a: `Prices are firm to rising. Limited supply and sustained demand support continued strength.` },
        { q: `How many developers are buying in ${suburb}?`, a: `Multiple developers are actively seeking sites. Competition is strong.` },
      ],
    },
    {
      id: `downsizing-${suburbSlug}-selling-developer`,
      category: 'Local Guide',
      title: `Downsizing in ${suburb}: How Selling to a Developer Funds Your Next Chapter`,
      excerpt: `Long-term ${suburb} homeowners could unlock significant value by selling to a developer rather than a home buyer.`,
      date: '2026-04-18',
      readTime: '6 min read',
      image: img('1656501382301-9b560230018a'),
      icon: Users,
      metaDescription: `Downsizing in ${suburb} Brisbane by selling to a developer. How long-term homeowners can unlock premium development value to fund retirement.`,
      content: `
## Your ${suburb} Home Could Fund a Comfortable Retirement

If you've owned your ${suburb} home for many years, the suburb's evolution means your property could be worth significantly more to a developer than to a traditional home buyer.

## The Opportunity

Properties in ${suburb}'s medium-to-high density zones regularly sell for **40-100% above standard residential value** as development sites. The premium could mean hundreds of thousands of additional dollars.

## Tax Benefits

If the property has been your principal residence, the capital gain is generally **exempt from CGT**. The Downsizer Contribution allows individuals aged 55+ to contribute up to $300,000 ($600,000 per couple) into superannuation.

## A Typical ${suburb} Scenario

| Factor | Standard Sale | Developer Sale |
|---|---|---|
| Property | 3-bed home on 600m² (MDR zone) | Same property |
| Buyer type | Home buyer | Developer |
| Sale price | $900,000 | $1,400,000 |
| Net difference | — | **+$500,000** |

## Common Concerns

### "I don't want to leave ${suburb}"
Many downsizers purchase a nearby apartment and stay in the community.

### "The process sounds complicated"
A specialist agent manages everything. You continue living in your home throughout.

### "How long before I need to move?"
Settlement is typically 6-18 months after contract — plenty of time.

## Next Steps

1. Request a free, confidential assessment from ACRES
2. Compare development vs residential value
3. Take your time — no rush, just information
4. Plan your next chapter with financial clarity

## Frequently Asked Questions

**Q: How much more could I get selling to a developer?**
Properties in suitable zones typically sell for 40-100% above residential value.

**Q: Do I pay tax on the sale?**
If it's been your principal residence, the gain is generally exempt from CGT.

**Q: Can I stay in ${suburb}?**
Yes. Many downsizers purchase a nearby apartment.

---

*Considering downsizing from your ${suburb} home? Call ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How much more could I get selling my ${suburb} home to a developer?`, a: `Properties in medium-to-high density zones typically sell for 40-100% above residential value.` },
        { q: `Do I pay CGT selling my ${suburb} home?`, a: `If it's been your principal residence, the gain is generally exempt from CGT.` },
        { q: `Can I stay in ${suburb} after selling?`, a: `Yes. Many downsizers purchase a nearby apartment and stay in the community.` },
      ],
    },
  ];
};

// ─── BATCH 4 SUBURBS (Outer 8km ring) ───────────────────────────────────────

export const greenslopesArticles = createSuburbArticles('Greenslopes', 'greenslopes', {
  distance: '4km south of',
  keyInfra: 'Greenslopes Private Hospital is a major healthcare employer generating sustained medical workforce housing demand. The PA Hospital is adjacent, creating a dual-hospital health precinct. Logan Road provides a bus transport corridor into the CBD. Proximity to the Stones Corner commercial precinct adds lifestyle amenity.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-45% above residential' },
    { name: 'Medium Density Residential', height: '4-6 storeys', product: 'Walk-up apartments', premium: 'Significant — 50-75% above residential' },
    { name: 'High Density (Logan Rd corridor)', height: '8-12+ storeys', product: 'Apartment buildings along Logan Road', premium: 'High — corridor premium' },
    { name: 'Centre (Stones Corner)', height: '6-10 storeys', product: 'Mixed-use, retail + residential', premium: 'High — commercial precinct premium' },
  ],
  mainStreets: 'Logan Road, Chatsworth Road, Juliette Street, and Cornwall Street',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$700K - $1.0M', perm2: '$1,500 - $1,900' },
    { type: 'MDR zone site', size: '500-700m²', range: '$950K - $1.5M', perm2: '$1,700 - $2,300' },
    { type: 'HDR site (Logan Rd)', size: '600-1,000m²', range: '$1.4M - $3.0M+', perm2: '$2,200 - $3,500' },
  ],
  keyDriver: 'Dual-hospital health precinct (Greenslopes Private + PA Hospital), Logan Road transport corridor, and Stones Corner commercial centre',
  uniqueFactor: 'Greenslopes is anchored by two major hospitals — Greenslopes Private Hospital and the adjacent Princess Alexandra Hospital — creating one of Brisbane\'s most significant health precincts. This dual-hospital cluster generates permanent, recession-resistant housing demand from medical professionals, researchers, and support staff.',
  comparison: 'Greenslopes offers excellent value relative to inner-south suburbs like Woolloongabba and Dutton Park. The health precinct provides a unique and reliable demand driver that supports consistent developer interest.',
  nearbyAmenity: 'Greenslopes Private Hospital, PA Hospital, Stones Corner shops, and Logan Road bus corridor',
});

export const coorparooArticles = createSuburbArticles('Coorparoo', 'coorparoo', {
  distance: '4km south-east of',
  keyInfra: 'Coorparoo train station provides direct CBD rail access. Coorparoo Square is a recently redeveloped shopping and lifestyle centre. The Stones Corner commercial precinct is adjacent. Proximity to the Gabba and Woolloongabba\'s Cross River Rail transformation creates spillover demand. Old Cleveland Road provides a major transport corridor.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 25-40% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 45-70% above residential' },
    { name: 'High Density (near station)', height: '6-10+ storeys', product: 'Apartment buildings', premium: 'High — station precinct premium' },
    { name: 'Centre (Coorparoo Square)', height: '6-10 storeys', product: 'Mixed-use, retail + residential', premium: 'High — lifestyle precinct' },
  ],
  mainStreets: 'Old Cleveland Road, Cavendish Road, Stanley Street East, and Bennetts Road',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$750K - $1.1M', perm2: '$1,600 - $2,100' },
    { type: 'MDR zone site', size: '500-700m²', range: '$1.0M - $1.6M', perm2: '$1,800 - $2,500' },
    { type: 'HDR site (near station)', size: '600-1,000m²', range: '$1.5M - $3.0M+', perm2: '$2,400 - $3,500' },
  ],
  keyDriver: 'Coorparoo train station, recently redeveloped Coorparoo Square, and spillover demand from Woolloongabba\'s Olympic and Cross River Rail transformation',
  uniqueFactor: 'Coorparoo is a well-established suburban centre experiencing a renaissance driven by the redeveloped Coorparoo Square lifestyle centre and proximity to the transforming Woolloongabba precinct. With a train station, established commercial centres, and excellent school catchments, Coorparoo attracts both family and investor end-buyers.',
  comparison: 'Coorparoo offers strong value compared to Woolloongabba and East Brisbane while sharing similar infrastructure access. The train station and Coorparoo Square anchor lifestyle appeal that supports premium end-product pricing.',
  nearbyAmenity: 'Coorparoo train station, Coorparoo Square, Stones Corner, and the Gabba precinct',
});

export const morningsideArticles = createSuburbArticles('Morningside', 'morningside', {
  distance: '5km east of',
  keyInfra: 'Morningside train station provides direct CBD rail access. The suburb sits between the premium Bulimba/Hawthorne lifestyle precinct to the east and the established Norman Park/East Brisbane corridor to the west. Wynnum Road provides a commercial corridor with neighbourhood retail and services.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 25-40% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 45-70% above residential' },
    { name: 'Centre (Wynnum Road)', height: '4-6 storeys', product: 'Mixed-use, neighbourhood retail + residential', premium: 'High — station and commercial precinct' },
  ],
  mainStreets: 'Wynnum Road, Thynne Road, Lytton Road, and Junction Street',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$750K - $1.1M', perm2: '$1,600 - $2,000' },
    { type: 'MDR zone site', size: '500-700m²', range: '$1.0M - $1.5M', perm2: '$1,800 - $2,400' },
    { type: 'Centre zone (Wynnum Rd)', size: '600m²+', range: '$1.3M - $2.5M+', perm2: '$2,200 - $3,200' },
  ],
  keyDriver: 'Morningside train station, spillover demand from premium Bulimba/Hawthorne lifestyle precinct, and relative affordability within the rail corridor',
  uniqueFactor: 'Morningside is a transitioning suburb that benefits from a powerful adjacency effect — sitting between the premium Bulimba/Hawthorne lifestyle precinct and established East Brisbane. Its train station provides direct CBD access, while relative affordability compared to neighbouring suburbs attracts developers seeking better-value sites.',
  comparison: 'Morningside offers the most affordable rail-connected development sites in Brisbane\'s inner east. As Bulimba and Hawthorne prices continue rising, Morningside represents the value play for developers targeting the eastern corridor.',
  nearbyAmenity: 'Morningside train station, Bulimba Oxford Street (nearby), Wynnum Road shops, and Norman Creek parklands',
});

export const bulimbaArticles = createSuburbArticles('Bulimba', 'bulimba', {
  distance: '4km east of',
  keyInfra: 'Oxford Street is one of Brisbane\'s premier suburban lifestyle strips, featuring independent dining, boutique retail, and weekend markets. The Bulimba ferry terminal provides river transport access to the CBD. The riverside walk connects through to Teneriffe and New Farm. Proximity to the Brisbane River provides premium waterfront positioning.',
  zones: [
    { name: 'Character Residential', height: '2 storeys (strict)', product: 'Sympathetic renovations, limited infill', premium: 'Limited — character protections' },
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-50% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Boutique walk-up apartments', premium: 'Significant — 50-80% above residential' },
    { name: 'Centre (Oxford Street)', height: '4-6 storeys', product: 'Mixed-use, boutique retail + residential', premium: 'High — lifestyle precinct premium' },
  ],
  mainStreets: 'Oxford Street, Riding Road, Junction Street, and Bulimba Street',
  priceRange: [
    { type: 'Character house lot', size: '400-600m²', range: '$1.2M - $2.0M (residential)', perm2: 'N/A — character protected' },
    { type: 'MDR site (no overlay)', size: '500-700m²', range: '$1.3M - $2.2M', perm2: '$2,400 - $3,200' },
    { type: 'Centre/Oxford St area', size: '500-800m²', range: '$2.0M - $4.0M+', perm2: '$3,200 - $5,000+' },
  ],
  keyDriver: 'Oxford Street lifestyle precinct, Bulimba ferry river access, and established reputation as one of Brisbane\'s most desirable family and lifestyle suburbs',
  uniqueFactor: 'Bulimba is one of Brisbane\'s premier lifestyle suburbs, anchored by the celebrated Oxford Street dining and retail strip. The suburb\'s riverfront positioning, ferry access, and village atmosphere create a distinct community identity that drives premium property values. Character protections cover significant areas, making available development sites rare and highly competitive.',
  comparison: 'Bulimba development sites command premiums reflecting the suburb\'s lifestyle positioning. Properties without character overlay in MDR zones are among the most sought-after in Brisbane\'s eastern corridor.',
  nearbyAmenity: 'Oxford Street lifestyle precinct, Bulimba ferry, Brisbane River walks, and Bulimba Memorial Park',
});

export const hawthorneArticles = createSuburbArticles('Hawthorne', 'hawthorne', {
  distance: '4km east of',
  keyInfra: 'Hawthorne ferry provides river transport to the CBD. Hawthorne Road offers a boutique commercial strip with cafes and neighbourhood retail. The riverside walk connects to Bulimba, New Farm, and the CBD. Proximity to Oxford Street (Bulimba) adds lifestyle appeal. The suburb\'s river frontage provides premium positioning.',
  zones: [
    { name: 'Character Residential', height: '2 storeys (restricted)', product: 'Sympathetic renovations, heritage infill', premium: 'Limited — character protections' },
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-45% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Boutique apartments', premium: 'Significant — 50-75% above residential' },
    { name: 'High Density (limited river)', height: '6-10+ storeys', product: 'Premium river-view apartments', premium: 'Very high — river premium' },
  ],
  mainStreets: 'Hawthorne Road, Riding Road, Lytton Road, and Grosvenor Street',
  priceRange: [
    { type: 'Character house lot', size: '400-600m²', range: '$1.1M - $1.8M (residential)', perm2: 'N/A — character protected' },
    { type: 'MDR site (no overlay)', size: '500-700m²', range: '$1.2M - $2.0M', perm2: '$2,200 - $3,000' },
    { type: 'River-adjacent / HDR', size: '600m²+', range: '$2.0M - $4.5M+', perm2: '$3,200 - $5,500+' },
  ],
  keyDriver: 'Brisbane River frontage, Hawthorne ferry access, established prestige residential positioning, and Bulimba Oxford Street lifestyle proximity',
  uniqueFactor: 'Hawthorne is a riverside suburb defined by its premium residential character, heritage Queenslanders, and ferry access to the CBD. The suburb\'s position between Bulimba\'s Oxford Street lifestyle precinct and the river creates a highly desirable address. Character protections cover many streets, making available development sites scarce and valuable.',
  comparison: 'Hawthorne development sites command premiums for their river positioning and prestige address. River-frontage sites compete with Teneriffe and Kangaroo Point for Brisbane\'s most premium waterfront development opportunities.',
  nearbyAmenity: 'Hawthorne ferry, Brisbane River walks, Bulimba Oxford Street, and Hawthorne Road shops',
});

export const windsorArticles = createSuburbArticles('Windsor', 'windsor', {
  distance: '4km north of',
  keyInfra: 'Windsor train station provides direct CBD rail access. Lutwyche Road is a major commercial corridor with bus services. The suburb is well-positioned between the CBD and Brisbane Airport via the Airport Link tunnel. Proximity to Newmarket and Albion provides additional amenity and employment access.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 25-40% above residential' },
    { name: 'Medium Density Residential', height: '4-6 storeys', product: 'Walk-up apartments', premium: 'Significant — 45-70% above residential' },
    { name: 'High Density (near station)', height: '8-12+ storeys', product: 'Apartment buildings', premium: 'High — station precinct premium' },
    { name: 'Centre (Lutwyche Road)', height: '6-10 storeys', product: 'Mixed-use, commercial + residential', premium: 'High — corridor premium' },
  ],
  mainStreets: 'Lutwyche Road, Bowen Bridge Road, Newmarket Road, and Eildon Road',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$700K - $1.0M', perm2: '$1,500 - $1,900' },
    { type: 'MDR zone site', size: '500-700m²', range: '$950K - $1.5M', perm2: '$1,700 - $2,300' },
    { type: 'HDR site (near station)', size: '600-1,000m²', range: '$1.4M - $3.0M+', perm2: '$2,200 - $3,500' },
  ],
  keyDriver: 'Windsor train station providing direct CBD access, Lutwyche Road commercial corridor, and relative affordability in the inner-north rail corridor',
  uniqueFactor: 'Windsor is a well-positioned inner-north suburb with the significant advantage of a train station and Lutwyche Road commercial corridor. Its relative affordability compared to nearby Herston, Wilston, and Albion — combined with excellent transport — makes it an increasingly attractive target for developers seeking value in the inner-north.',
  comparison: 'Windsor offers excellent value within the inner-north rail corridor. Development site pricing is competitive while benefiting from the same rail connectivity as nearby higher-priced suburbs.',
  nearbyAmenity: 'Windsor train station, Lutwyche Road shops, Newmarket shopping, and Royal Brisbane Hospital precinct',
});

export const wilstonArticles = createSuburbArticles('Wilston', 'wilston', {
  distance: '4km north of',
  keyInfra: 'Wilston train station provides direct CBD rail access. Wilston Village is a charming neighbourhood commercial centre with cafes, boutiques, and community services. Kedron Brook provides green space corridors. The suburb benefits from proximity to the Royal Brisbane Hospital and Kelvin Grove Urban Village employment centres.',
  zones: [
    { name: 'Character Residential', height: '2 storeys (restricted)', product: 'Sympathetic renovations, limited infill', premium: 'Limited — character protections' },
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 25-40% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 45-70% above residential' },
    { name: 'Centre (Wilston Village)', height: '3-5 storeys', product: 'Mixed-use, village retail + residential', premium: 'High — village precinct premium' },
  ],
  mainStreets: 'Kedron Park Road, Wilmington Street, Macgregor Street, and Dawson Street',
  priceRange: [
    { type: 'Character house lot', size: '400-600m²', range: '$1.0M - $1.6M (residential)', perm2: 'N/A — character protected' },
    { type: 'MDR site (no overlay)', size: '500-700m²', range: '$1.1M - $1.7M', perm2: '$2,000 - $2,700' },
    { type: 'Centre zone (Village)', size: '500-800m²', range: '$1.5M - $3.0M+', perm2: '$2,500 - $3,800' },
  ],
  keyDriver: 'Wilston train station, charming Wilston Village centre, and strong family demographic creating premium end-buyer demand',
  uniqueFactor: 'Wilston is one of Brisbane\'s most charming inner-north suburbs — a village-feel neighbourhood with a delightful commercial centre, train station, and strong community identity. Character protections cover significant areas, reflecting the suburb\'s heritage Queenslander streetscapes. Development sites outside the character overlay are rare and highly valued.',
  comparison: 'Wilston\'s village character creates a premium positioning. Development sites outside character overlay areas command strong prices reflecting the suburb\'s family-friendly reputation and lifestyle appeal.',
  nearbyAmenity: 'Wilston train station, Wilston Village shops, Kedron Brook, and RBWH hospital precinct',
});

export const lutwycheArticles = createSuburbArticles('Lutwyche', 'lutwyche', {
  distance: '5km north of',
  keyInfra: 'Lutwyche Road is a major commercial and transport corridor with high-frequency bus services into the CBD. Lutwyche City shopping centre provides established retail amenity. The Northern Busway provides bus rapid transit access. Proximity to Windsor train station (adjacent suburb) provides additional rail connectivity. The Airport Link tunnel entrance is nearby.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 25-40% above residential' },
    { name: 'Medium Density Residential', height: '4-6 storeys', product: 'Walk-up apartments', premium: 'Significant — 40-65% above residential' },
    { name: 'High Density (Lutwyche Rd)', height: '8-15+ storeys', product: 'Apartment towers along Lutwyche Road', premium: 'High — corridor premium' },
    { name: 'Centre (Lutwyche City)', height: '8-15 storeys', product: 'Mixed-use, retail + residential towers', premium: 'High — commercial precinct premium' },
  ],
  mainStreets: 'Lutwyche Road, Kedron Park Road, Bradshaw Street, and Kitchener Road',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$650K - $950K', perm2: '$1,400 - $1,800' },
    { type: 'MDR zone site', size: '500-700m²', range: '$900K - $1.4M', perm2: '$1,600 - $2,200' },
    { type: 'HDR site (Lutwyche Rd)', size: '600-1,000m²', range: '$1.3M - $3.0M+', perm2: '$2,000 - $3,200' },
  ],
  keyDriver: 'Lutwyche Road commercial corridor with HDR zoning, Northern Busway access, and the most affordable development site pricing in Brisbane\'s inner-north',
  uniqueFactor: 'Lutwyche offers the best value proposition for development sites in Brisbane\'s inner north. The Lutwyche Road corridor carries significant HDR and Centre zoning, while residential streets provide more affordable entry points. As prices in Windsor, Albion, and other inner-north suburbs escalate, Lutwyche is the natural next frontier for developer activity.',
  comparison: 'Lutwyche offers the most affordable inner-north development sites while benefiting from strong transport connectivity via the Northern Busway and adjacent Windsor train station. The value gap with neighbouring suburbs is narrowing as developer interest intensifies.',
  nearbyAmenity: 'Lutwyche City shopping, Northern Busway, Windsor train station (adjacent), and Kedron Brook',
});
